/**
 * Human-readable dimensions for an imported asset, e.g. `1080 × 1350 · 4:5`.
 *
 * Mixed aspect ratios are deliberate here (varied formats are the ad strategy), so
 * this is neutral information, never a warning — no value gets special styling or
 * flagging. It exists so an AE can tell at a glance which format an asset actually
 * is, which the old square crop hid.
 */

/** Ratios worth naming, so near-misses from export tooling don't show as 199:249. */
const NAMED_RATIOS: { label: string; value: number }[] = [
  { label: '1:1', value: 1 },
  { label: '4:5', value: 4 / 5 },
  { label: '9:16', value: 9 / 16 },
  { label: '16:9', value: 16 / 9 },
  { label: '1.91:1', value: 1.91 },
  { label: '2:3', value: 2 / 3 },
  { label: '3:2', value: 3 / 2 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
]

/** Within 1% of a named ratio counts as that ratio. */
const SNAP_TOLERANCE = 0.01

/** Beyond this, the nearest named ratio is too far off to be worth naming. */
const APPROX_TOLERANCE = 0.06

/**
 * The nearest named ratio, prefixed with `~` when the asset is close to but not
 * exactly one. Returns null when nothing is close enough — a reduced raw ratio is
 * useless for real-world exports (a 1003×1568 asset shares no factors, so it
 * "reduces" to 1003:1568 and just repeats the dimensions).
 */
export function aspectRatioLabel(width: number, height: number): string | null {
  if (!width || !height) return null
  const ratio = width / height
  const distance = (r: { value: number }) => Math.abs(ratio - r.value) / r.value
  const nearest = NAMED_RATIOS.reduce((best, r) => (distance(r) < distance(best) ? r : best))
  const off = distance(nearest)
  if (off <= SNAP_TOLERANCE) return nearest.label
  if (off <= APPROX_TOLERANCE) return `~${nearest.label}`
  return null
}

export function dimensionsLabel(width: number, height: number): string | null {
  if (!width || !height) return null
  const ratio = aspectRatioLabel(width, height)
  return ratio ? `${width} × ${height} · ${ratio}` : `${width} × ${height}`
}
